import { cn } from "@/lib/utils"
import { criterionTone, TONE_WASH_CLASS } from "@/lib/gng-score"

interface ScoreBarProps {
  value: number | null
  onChange?: (value: number | null) => void
  max?: number
  readOnly?: boolean
  /** Tint the selected score by quality (1 red, 2 amber, 3 neutral, 4–5 green). */
  semantic?: boolean
  /** Render an azure tick above the score AI suggested, so agree/override is visible. */
  aiSuggestedScore?: number | null
}

export function ScoreBar({
  value,
  onChange,
  max = 5,
  readOnly = false,
  semantic = false,
  aiSuggestedScore = null,
}: ScoreBarProps) {
  const handleClick = (segment: number) => {
    if (readOnly || !onChange) return
    onChange(value === segment ? null : segment)
  }

  const washClass =
    semantic && value != null ? TONE_WASH_CLASS[criterionTone(value)] : TONE_WASH_CLASS.neutral
  const hasTick = aiSuggestedScore != null && aiSuggestedScore >= 1 && aiSuggestedScore <= max

  return (
    <div className="flex items-center gap-2" role="radiogroup" aria-label="Score">
      <div
        className={cn(
          "inline-flex rounded-lg border border-input bg-background",
          hasTick && "mt-2",
        )}
      >
        {Array.from({ length: max }, (_, i) => i + 1).map((segment) => {
          const isSelected = value === segment
          return (
            <button
              key={segment}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={`Score ${segment}`}
              disabled={readOnly}
              onClick={() => handleClick(segment)}
              className={cn(
                "relative h-7 w-8 text-xs tabular-nums transition-colors",
                "border-l border-border first:border-l-0",
                "first:rounded-l-[7px] last:rounded-r-[7px]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:z-10",
                isSelected ? cn("font-medium text-foreground", washClass) : "text-muted-foreground",
                !readOnly && !isSelected && "hover:bg-accent hover:text-foreground",
                !readOnly && "cursor-pointer",
                readOnly && "cursor-default",
              )}
            >
              {hasTick && segment === aiSuggestedScore && (
                <span
                  role="img"
                  title={`AI suggested ${aiSuggestedScore}`}
                  aria-label={`AI suggested ${aiSuggestedScore}`}
                  className="absolute -top-[9px] left-1/2 h-0 w-0 -translate-x-1/2 border-x-4 border-x-transparent border-t-[5px] border-t-[hsl(var(--brand))]"
                />
              )}
              {segment}
            </button>
          )
        })}
      </div>
      {/* Once scored, the selected segment already shows the number — a
          trailing "N / 5" said it twice. "Not scored" stays: with no segment
          selected, nothing else says so. */}
      {value == null && <span className="text-xs text-muted-foreground">Not scored</span>}
    </div>
  )
}
